import type { ExplicitAddress } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Token } from '@ui/config/token';
import { useSDK } from '@ui/utils';
import type { Fraction } from 'bi-fraction';

import { useAgents } from '../api/useAgents';
import { usePriceHistory } from '../api/usePriceHistory';
import { useSearchAgents } from '../api/useSearchAgents';
import { getTokenBalanceQueryKey } from '../contracts/useTokenBalance';
import { getTokenPriceQueryKey } from '../tokenPrice';
import { useAgentfunRemaining } from './useAgentfunRemaining';

export const useBondingCurveTrade = (
  mode: 'buy' | 'sell',
  onSuccess?: (result: { hash?: string; returnedAmount?: Fraction }) => void,
) => {
  const sdk = useSDK();
  const provider = currentWallet.useProvider();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      tokenAddress,
      amount,
    }: {
      tokenAddress: ExplicitAddress;
      amount: Fraction;
    }) => {
      const signer = await provider?.getSigner();
      if (!signer) throw new Error('no signer');
      const bondingCurveModule = sdk.getBondingCurveModule(signer);
      const result = await (mode === 'buy'
        ? bondingCurveModule.buyTokens(amount, tokenAddress)
        : bondingCurveModule.sellTokens(amount, tokenAddress));
      return result;
    },
    {
      onSuccess: (result, { tokenAddress }) => {
        queryClient.invalidateQueries({
          queryKey: getTokenBalanceQueryKey(sdk.getContractAddress('AgentFun')),
        });
        queryClient.invalidateQueries({
          queryKey: getTokenBalanceQueryKey(tokenAddress),
        });
        if (mode === 'buy')
          queryClient.invalidateQueries(['useGraduatedTokens']);
        queryClient.invalidateQueries({
          queryKey: getTokenPriceQueryKey({
            type: Token.Bonding,
            bondingAddress: tokenAddress,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: ['useBondingTokenOrAgentTokenPrice'],
        });
        queryClient.invalidateQueries([useAgents.queryKey]);
        queryClient.invalidateQueries({
          queryKey: [useSearchAgents.queryKey],
        });
        queryClient.invalidateQueries(['useAgent']);
        queryClient.invalidateQueries([usePriceHistory.queryKey]);
        queryClient.invalidateQueries([useAgentfunRemaining.queryKey]);
        const hash = result.receipt?.hash;
        onSuccess?.({ hash, returnedAmount: result.returnedAmount });
      },
    },
  );
};
